import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import * as FactoryListActions from './factory-config.actions';
import { TFactoryConfigActionType } from './factory-config.models';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import { of, from, forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { FactoryConfigFacade } from './factory-config.facade';

@Injectable({ providedIn: 'root' })
export class FactoryConfigEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private router: Router,
    private factoryConfigFacade: FactoryConfigFacade,
  ) {}

  getAllFactoryConfigs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetAllFactoryConfigs),
      switchMap(() =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl] as const;
          }),
        ),
      ),
      switchMap(([loaderEl]) =>
        forkJoin({
          efficiencyConfig: this.plani8DataService.fetchFactoryEfficiencyConfigList().pipe(catchError(() => of(null))),
          benchmarkingConfig: this.plani8DataService
            .fetchFactoryBenchmarkingConfigList()
            .pipe(catchError(() => of(null))),
          otlOplConfig: this.plani8DataService.fetchFactoryOtlOplConfigList().pipe(catchError(() => of(null))),
          sedConfig: this.plani8DataService.fetchFactorySEDConfig().pipe(catchError(() => of(null))),
          expectedDateConfig: this.plani8DataService.fetchFactoryExpectedDateConfig().pipe(catchError(() => of(null))),
          tnaTargetUpdatesConfig: this.plani8DataService.fetchTnaTargetUpdatesConfig().pipe(catchError(() => of(null))),
        }).pipe(
          finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)),
          map(
            ({
              efficiencyConfig,
              benchmarkingConfig,
              otlOplConfig,
              sedConfig,
              expectedDateConfig,
              tnaTargetUpdatesConfig,
            }) => {
              return new FactoryListActions.GetAllFactoryConfigsSuccess(
                efficiencyConfig,
                benchmarkingConfig,
                otlOplConfig,
                sedConfig,
                expectedDateConfig,
                tnaTargetUpdatesConfig,
              );
            },
          ),
          catchError(() => {
            return of(new FactoryListActions.GetAllFactoryConfigsFailure());
          }),
        ),
      ),
    ),
  );

  getFactoryEfficiencyConfigList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetFactoryEfficiencyConfig),
      switchMap(() => {
        this.factoryConfigFacade.setEfficiencyLoader(true);
        return this.plani8DataService.fetchFactoryEfficiencyConfigList().pipe(
          map((res) => new FactoryListActions.GetFactoryEfficiencyConfigSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetFactoryEfficiencyConfigFailure());
          }),
          finalize(() => {
            this.factoryConfigFacade.setEfficiencyLoader(false);
          }),
        );
      }),
    ),
  );

  updateFactoryEfficiencyConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.UpdateFactoryEfficiencyConfig),
      switchMap((action: FactoryListActions.UpdateFactoryEfficiencyConfig) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateFactoryEfficiencyConfig(action.payload).pipe(
          finalize(() => {
            this.factoryConfigFacade.fetchAllFactoryConfigs();
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new FactoryListActions.UpdateFactoryEfficiencyConfigSuccess()),
          catchError(() => {
            return of(new FactoryListActions.UpdateFactoryEfficiencyConfigFailure());
          }),
        ),
      ),
    ),
  );

  getFactoryBenchmarkingConfigList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetFactoryBenchmarkingConfig),
      switchMap(() => {
        this.factoryConfigFacade.setBenchmarkingLoader(true);
        return this.plani8DataService.fetchFactoryBenchmarkingConfigList().pipe(
          map((res) => new FactoryListActions.GetFactoryBenchmarkingConfigSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetFactoryBenchmarkingConfigFailure());
          }),
          finalize(() => {
            this.factoryConfigFacade.setBenchmarkingLoader(false);
          }),
        );
      }),
    ),
  );

  updateFactoryBenchmarkingConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.UpdateFactoryBenchmarkingConfig),
      switchMap((action: FactoryListActions.UpdateFactoryBenchmarkingConfig) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateFactoryBenchmarkingConfig(action.payload).pipe(
          finalize(() => {
            this.factoryConfigFacade.fetchAllFactoryConfigs();
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new FactoryListActions.UpdateFactoryBenchmarkingConfigSuccess()),
          catchError(() => {
            return of(new FactoryListActions.UpdateFactoryBenchmarkingConfigFailure());
          }),
        ),
      ),
    ),
  );

  getFactoryOtlOplConfigList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetFactoryOtlOplConfig),
      switchMap(() => {
        this.factoryConfigFacade.setOtlOplLoader(true);
        return this.plani8DataService.fetchFactoryOtlOplConfigList().pipe(
          map((res) => new FactoryListActions.GetFactoryOtlOplConfigSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetFactoryOtlOplConfigFailure());
          }),
          finalize(() => {
            this.factoryConfigFacade.setOtlOplLoader(false);
          }),
        );
      }),
    ),
  );

  updateFactoryOtlOplConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.UpdateFactoryOtlOplConfig),
      switchMap((action: FactoryListActions.UpdateFactoryOtlOplConfig) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateFactoryOtlOplConfig(action.payload).pipe(
          finalize(() => {
            this.factoryConfigFacade.fetchAllFactoryConfigs();
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new FactoryListActions.UpdateFactoryOtlOplConfigSuccess()),
          catchError(() => {
            return of(new FactoryListActions.UpdateFactoryOtlOplConfigFailure());
          }),
        ),
      ),
    ),
  );

  getFactorySedConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetFactorySedConfig),
      switchMap(() => {
        this.factoryConfigFacade.setSedLoader(true);
        return this.plani8DataService.fetchFactorySEDConfig().pipe(
          map((res) => new FactoryListActions.GetFactorySedConfigSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetFactorySedConfigFailure());
          }),
          finalize(() => {
            this.factoryConfigFacade.setSedLoader(false);
          }),
        );
      }),
    ),
  );

  updateFactorySedConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.UpdateFactorySedConfig),
      switchMap((action: FactoryListActions.UpdateFactorySedConfig) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateFactorySEDConfig(action.payload).pipe(
          finalize(() => {
            this.factoryConfigFacade.fetchAllFactoryConfigs();
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new FactoryListActions.UpdateFactorySedConfigSuccess()),
          catchError(() => {
            return of(new FactoryListActions.UpdateFactorySedConfigFailure());
          }),
        ),
      ),
    ),
  );

  getFactoryExpectedDateConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetFactoryExpectedDateConfig),
      switchMap(() => {
        this.factoryConfigFacade.setExpectedDateLoader(true);
        return this.plani8DataService.fetchFactoryExpectedDateConfig().pipe(
          map((res) => new FactoryListActions.GetFactoryExpectedDateConfigSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetFactoryExpectedDateConfigFailure());
          }),
          finalize(() => {
            this.factoryConfigFacade.setExpectedDateLoader(false);
          }),
        );
      }),
    ),
  );

  updateFactoryExpectedDateConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.UpdateFactoryExpectedDateConfig),
      switchMap((action: FactoryListActions.UpdateFactoryExpectedDateConfig) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateFactoryExpectedDateConfig(action.payload).pipe(
          finalize(() => {
            this.factoryConfigFacade.fetchAllFactoryConfigs();
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new FactoryListActions.UpdateFactoryExpectedDateConfigSuccess()),
          catchError(() => {
            return of(new FactoryListActions.UpdateFactoryExpectedDateConfigFailure());
          }),
        ),
      ),
    ),
  );

  getTnaTargetUpdatesConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.GetTnaTargetUpdatesConfig),
      switchMap(() => {
        this.factoryConfigFacade.setTnaTargetUpdatesConfigLoader(true);
        return this.plani8DataService.fetchTnaTargetUpdatesConfig().pipe(
          map((res) => new FactoryListActions.GetTnaTargetUpdatesConfigSuccess(res)),
          catchError(() => {
            return of(new FactoryListActions.GetTnaTargetUpdatesConfigFailure());
          }),
          finalize(() => {
            this.factoryConfigFacade.setTnaTargetUpdatesConfigLoader(false);
          }),
        );
      }),
    ),
  );

  updateTnaTargetUpdatesConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TFactoryConfigActionType.UpdateTnaTargetUpdatesConfig),
      switchMap((action: FactoryListActions.UpdateTnaTargetUpdatesConfig) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateTnaTargetConfig(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.appNotificationUtilClass.presentToast('Success', 'TNA Target Update setting saved successfully.');
            this.factoryConfigFacade.fetchAllFactoryConfigs();
            return new FactoryListActions.UpdateTnaTargetUpdatesConfigSuccess();
          }),
          catchError(() => {
            return of(new FactoryListActions.UpdateTnaTargetUpdatesConfigFailure());
          }),
        ),
      ),
    ),
  );
}
